import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import diaryEmptyImg from '@/assets/images/dashboard-tiles/2.webp';
import { countUnread } from '@/components/Dashboard/countUnread';
import { DiaryModal } from '@/components/Dashboard/DiaryModal';
import { DiaryIcon } from '@/components/Icons/DashboardIcons';
import { DashboardTile } from '@/components/Dashboard/DashboardTile';
import { EmptyTileState } from '@/components/Dashboard/EmptyTileState';
import { SentimentBadge } from '@/components/SentimentBadge';
import { TileLoadingState } from '@/components/Dashboard/TileLoadingState';
import { TileSubscriptionPaywall } from '@/components/Dashboard/TileSubscriptionPaywall';
import { UnreadDot } from '@/components/Dashboard/UnreadDot';
import { DashboardTileType } from '@/types/components';
import { useTeacherMessages } from '@/components/Dashboard/hooks/useTeacherMessages';
import { useHasFamilySubscription } from '@/hooks/useHasFamilySubscription';
import { formatShortDate, sortByDateDesc } from '@/utils/date';
import { linkifyText } from '@/utils/html';
import { BULLET_SYMBOL } from '@/utils/text';

const DIARY_LIMIT = 3;

export const DiaryTile: React.FC = () => {
  const { t } = useTranslation();
  const hasSubscription = useHasFamilySubscription();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data, isLoading } = useTeacherMessages({ enabled: hasSubscription });
  const allMessages = data ?? [];
  const messages = sortByDateDesc(allMessages, ({ date }) => date).slice(
    0,
    DIARY_LIMIT,
  );
  const isEmpty = messages.length === 0;
  const hasOlderMessages = allMessages.length > DIARY_LIMIT;
  const badgeCount = hasSubscription ? countUnread(allMessages) : 0;

  return (
    <>
      <DashboardTile
        title={t('home.dashboard.diary.title')}
        footerButtonLabel={t('home.dashboard.diary.viewOlder')}
        type={DashboardTileType.Diary}
        icon={DiaryIcon}
        badgeCount={badgeCount}
        hideFooter={!hasSubscription || isLoading || !hasOlderMessages}
        isCompact={!hasSubscription}
        onFooterButtonClick={() => setIsModalOpen(true)}
      >
        {!hasSubscription && (
          <TileSubscriptionPaywall
            imageSrc={diaryEmptyImg}
            description={t('home.dashboard.diary.subscriptionEmpty')}
          />
        )}
        {hasSubscription && (
          <>
            {isLoading && <TileLoadingState />}
            {!isLoading && isEmpty && <EmptyTileState />}
            {!isLoading && !isEmpty && (
              <ul className="flex flex-col">
                {messages.map((message) => (
                  <li
                    key={message.id}
                    className="flex flex-col border-b border-border px-4 py-3 last:border-b-0"
                  >
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className="text-caption-12 bg-on-primary-surface min-w-0 truncate rounded-xl px-2 py-0.5 tracking-[0.025em] text-foreground">
                        {message.typeName}
                      </span>
                      <span className="text-body-13 md:text-body-14 flex shrink-0 items-center gap-1 text-subtle-foreground">
                        {formatShortDate(message.date)}
                        {message.isNew && <UnreadDot />}
                      </span>
                    </div>
                    <div className="mb-[6px] flex items-center justify-between gap-3">
                      <div className="text-subhead-14 flex min-w-0 flex-1 items-center gap-1 truncate text-foreground">
                        <span>{message.authorName}</span>
                        {message.behaviorEntry && (
                          <>
                            <span className="text-subtle-foreground">
                              {BULLET_SYMBOL}
                            </span>
                            <span>
                              ({message.behaviorEntry.disciplineName})
                            </span>
                          </>
                        )}
                      </div>
                      {message.behaviorEntry && (
                        <SentimentBadge type={message.behaviorEntry.type} />
                      )}
                    </div>
                    <p
                      className="rich-text text-body-13 md:text-body-14 truncate text-foreground"
                      dangerouslySetInnerHTML={{
                        __html: linkifyText(message.text),
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </DashboardTile>
      {isModalOpen && (
        <DiaryModal isOpen onClose={() => setIsModalOpen(false)} />
      )}
    </>
  );
};
